import { ApolloError, gql } from '@apollo/client';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { GraphQLErrors } from '@apollo/client/errors';
import { Box, Button, Link, Tag, useDisclosure, VStack } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { isNil } from 'lodash';
import isNumber from 'lodash/isNumber';
import uniq from 'lodash/uniq';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Shield } from '~auth/Shield';
import { useAuth } from '~auth/useAuth';
import {
  BulkActionModal_CustomerFragment,
  BulkActionModal_DisplayFragment,
  BulkActionModal_OrganizationFragment,
} from '~components/displays/BulkAction/__generated__/BulkActionModal.graphql';
import { BulkActionButton } from '~components/displays/BulkAction/BulkActionButton';
import { BulkActionModal } from '~components/displays/BulkAction/BulkActionModal';
import {
  BulkClaimDefaultConfig_CustomerFragment,
  BulkClaimDisplayModal_CustomerFragment,
} from '~components/displays/ClaimDisplay/__generated__/BulkClaimDisplayModal.graphql';
import { BulkClaimModal } from '~components/displays/ClaimDisplay/BulkClaimDisplayModal';
import { ClaimDisplayModal } from '~components/displays/ClaimDisplay/ClaimDisplayModal';
import { SelectClaimModal } from '~components/displays/ClaimDisplay/selectClaimModal';
import {
  DisplayTableServerSide,
  useDisplayTableServerSide,
} from '~components/displays/DisplayTable';
import { Columns, FilterOptionServerSide } from '~components/displays/DisplayTable/constants';
import {
  DisplayTableContext,
  DisplayTableProvider,
} from '~components/displays/DisplayTable/DisplayTableContext';
import { useFilterOptionsServerSide } from '~components/displays/DisplayTable/Filtering/useFilterOptionsServerSide';
// import { getFiltersFromUrl,useDisplaysQuery } from '~components/displays/DisplayTable/useDisplaysQuery';
import {
  FilterContext,
  getPersistentFilters,
} from '~components/displays/DisplayTable/persistantFilters/persistantFilters';
import { DisplaysQueryContext } from '~components/displays/DisplayTable/useDisplaysQuery';
import { EmptyView, EmptyViewButton } from '~components/EmptyView';
import UpgradeToWaveEssentialModal from '~components/organization/UpgradeToWaveEssentialModal/UpgradeToWaveEssentialModal';
import { StatisticsItem } from '~components/statistics/StatisticsBar';
import UsageStatistics from '~components/statistics/UsageStatistics';
import { BackButton } from '~components/ui/BackButton';
import { HelpIcon } from '~components/ui/icons';
import { EmptyDisplayIllustration } from '~components/ui/illustrations/EmptyDisplayIllustration';
import { Page } from '~components/ui/Page';
import { PageActions } from '~components/ui/PageActions';
import { PageContent } from '~components/ui/PageContent';
import { PageHeading } from '~components/ui/PageHeading';
import { PageLoader } from '~components/ui/PageLoader';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { Exact } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { useAnalyticsReporter } from '~utils/analytics';
import { useFeatureFlag } from '~utils/features';
import { useSubscriptionType } from '~utils/subscriptions';
import { ensure, isDefined } from '~utils/types';
import {
  CustomerDisplaysIndexPageQuery,
  useCustomerDisplaysIndexPageQuery,
} from './__generated__/Index.graphql';

export type DataWithCustomer = Omit<CustomerDisplaysIndexPageQuery, 'customer'> & {
  customer: CustomerDisplaysIndexPageQuery['customer'];
};
type DisplayOverviewHeadingPropsServerSide = {
  data: DataWithCustomer;
  filtersState: DisplayFilters;
  handleSetFilters: (
    filterId: string,
    newValue: FilterOptionServerSide[],
    resetPagination?: boolean,
  ) => void;
  resetFilters: () => void;
};
export type DisplayFilters = Array<{ id: string; value: FilterOptionServerSide[] }>;

type CustomerDisplaysOverviewPropsServerSide = {
  data: DataWithCustomer;
  error: ApolloError | undefined;
  refetch: (
    variables?: Partial<Exact<{ customerHandle: string }>>,
  ) => Promise<ApolloQueryResult<CustomerDisplaysIndexPageQuery>>;
  pageIndex: number;
  pageSize: number;
  filters: DisplayFilters;
  totalRowCount: number;
  totalDisplays: number;
  search: string;
  handleSetSearch: (searchText: string) => void;
  handleSetPageSize: (pageSize: number) => void;
  handleSetPageIndex: (pageIndex: number) => void;
  handleSetFilters: (
    filterId: string,
    newValue: FilterOptionServerSide[],
    resetPagination?: boolean,
  ) => void;
  /** Callback when initial persisted filters have been applied */
  onFiltersReady?: (isReady: boolean) => void;
};

function DisplayOverviewHeadingServerSide({
  data,
  filtersState,
  handleSetFilters,
  resetFilters,
}: DisplayOverviewHeadingPropsServerSide) {
  const navigate = useNavigate();
  const { setDisplaysQuery } = useContext(DisplaysQueryContext);
  const tableContext = useContext(DisplayTableContext);

  if (isNil(tableContext)) {
    throw new Error('TableContext can only be used within TableContext Provider');
  }

  const { table } = tableContext;

  const handleGoBack = () => {
    navigate('/');
  };
  const handleGoToClaimCode = () => {
    if (!setDisplaysQuery) return;
    setDisplaysQuery(new URLSearchParams());
    navigate('../displays/claimCode');
  };
  const { hasWaveSubscription } = useSubscriptionType(data.customer);

  const numberOfDisplays = data?.customer?.displays?.length ?? 0;

  const shouldShowStatistics = hasWaveSubscription && numberOfDisplays > 0;
  const filters = useFilterOptionsServerSide(filtersState ?? [], data);

  const handleItemClicked = (item: StatisticsItem) => {
    const itemCanApplyFilter = ['Connected', 'Disconnected', 'Standby'].includes(item.name);

    if (itemCanApplyFilter) {
      table?.setAllFilters([
        {
          id: Columns.Site,
          value: [],
        },
        {
          id: Columns.Groups,
          value: [],
        },
        {
          id: Columns.Firmware,
          value: [],
        },
        {
          id: Columns.Playlist,
          value: [],
        },
        {
          id: Columns.PowerSchedule,
          value: [],
        },
        {
          id: Columns.Warnings,
          value: [],
        },
        {
          id: Columns.Status,
          value: [],
        },
        {
          id: Columns.Configuration,
          value: [],
        },
      ]);
      table?.setFilter(
        Columns.Status,
        filters.status.all.filter((f) => f.value === item.name),
      );
    }
    if (itemCanApplyFilter) {
      resetFilters();
      handleSetFilters(
        'status',
        filters.status.all.filter((f) => f.value === item.name),
      );
    }
  };

  async function handleClaimCodeClicked() {
    navigate(`claimCode`, { state: { canGoBack: true } });
  }
  const { displaysQuery } = useContext(DisplaysQueryContext);
  const claimCodeParam = displaysQuery.get('claimCode');
  let code = '';
  let name = '';
  if (claimCodeParam) {
    [code, name] = claimCodeParam.split('|');
  }
  const claimCodeHistoryButton = (
    <Button variant="solid" colorScheme="blue" onClick={handleClaimCodeClicked}>
      {t('viewClaimCodeHistory')}
    </Button>
  );
  return (
    <>
      {claimCodeParam ? (
        <PageHeading
          floatingButton={<BackButton onClick={handleGoToClaimCode} />}
          description={t('devicesClaimedClaimCode')}
        >
          {code}
          <Tag variant="outline" size="sm" mt="3" ml="8">
            {name}
          </Tag>
        </PageHeading>
      ) : hasWaveSubscription ? (
        <PageHeading
          floatingButton={<BackButton onClick={handleGoBack} />}
          actions={claimCodeHistoryButton}
        >
          {t('displayOverview')}
        </PageHeading>
      ) : (
        <PageHeading floatingButton={<BackButton onClick={handleGoBack} />}>
          {t('displayOverview')}
        </PageHeading>
      )}
      {claimCodeParam
        ? null
        : shouldShowStatistics && (
            <UsageStatistics customer={data?.customer} onItemClicked={handleItemClicked} />
          )}
    </>
  );
}

function DisplayOverviewServerSide({
  data,
  error,
  refetch,
  pageIndex,
  pageSize,
  filters,
  handleSetPageSize,
  handleSetPageIndex,
  totalRowCount,
  totalDisplays,
  handleSetFilters,
  search,
  handleSetSearch,
  onFiltersReady,
}: CustomerDisplaysOverviewPropsServerSide) {
  const { customer } = useParams();
  const navigate = useNavigate();
  const claimDisplayModal = useDisclosure();
  const bulkActionModal = useDisclosure();
  const upgradeToWaveEssentialModal = useDisclosure();
  const analytics = useAnalyticsReporter();
  const { verifyUserPermissions } = useAuth();
  const bulkClaimModal = useDisclosure();

  // Remove once subscriptions are released to everyone: https://inthepocket.atlassian.net/browse/TPVWAVE-1460
  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');
  const { hasLiteSubscription, hasWaveSubscription } = useSubscriptionType(data.customer);

  const table = useDisplayTableServerSide({
    data,
    pageIndex,
    pageSize,
    totalRowCount,
    hasWaveSubscription,
  });

  const tableContext = useContext(DisplayTableContext);

  if (isNil(tableContext)) {
    throw new Error('TableContext can only be used within TableContext Provider');
  }

  const [bulkClaimCode, setBulkClaimCode] = useState<string>('');
  const [bulkClaimId, setBulkClaimId] = useState<string>('');

  const { setTable } = tableContext;

  useEffect(() => {
    setTable?.(table);
  }, [table, setTable]);

  const selectedCount = table.selectedFlatRows.length;

  async function handleDisplayClicked(id: string) {
    analytics.track('displayDetail');
    navigate(id);
  }

  async function handleClaimDisplay() {
    analytics.track('displayClaimStart');
    setIsSelectModalOpen(true);
  }
  async function handleClose() {
    setIsSingleClaimModalOpen(false);
  }
  async function handleCreateSuccess({ id, code }: { id: string; code: string }) {
    setIsSelectModalOpen(false);
    setIsBulkClaimModalOpen(true);
    setBulkClaimCode(code);
    setBulkClaimId(id);
  }

  async function handleMultiClaimCodeActivate() {
    setIsBulkClaimModalOpen(false);
  }

  async function handleClaimDisplaySucceeded(display: { id: string }) {
    analytics.track('displayClaimComplete');
    claimDisplayModal.onClose();
    await refetch({ customerHandle: customer });
    navigate(display.id);
  }

  async function handleBulkActionSucceeded() {
    bulkActionModal.onClose();
    await refetch({ customerHandle: customer });
  }

  const extractInvalidDisplayIds = (graphQLErrors: GraphQLErrors | undefined): string[] => {
    if (!graphQLErrors) {
      return [];
    }

    const invalidDisplayIds = graphQLErrors?.map(({ path }) => {
      if (!path) {
        return;
      }

      const [c, d, index] = path;
      if (c === 'customer' && d === 'displays' && isNumber(index)) {
        return data?.customer?.displays[index as number]?.id;
      }
    });

    return uniq(invalidDisplayIds?.filter((id) => isDefined(id))) as string[];
  };

  const openBulkActions = () => {
    // Remove once subscriptions are released to everyone: https://inthepocket.atlassian.net/browse/TPVWAVE-1460
    if (isSubscriptionsEnabled && hasLiteSubscription) {
      upgradeToWaveEssentialModal.onOpen();
      return;
    }

    analytics.track('displayOpenBulkActions');
    bulkActionModal.onOpen();
  };

  const hasDisplayClaimPermission = verifyUserPermissions([Permission.DisplayClaim]);
  const { displaysQuery } = useContext(DisplaysQueryContext);
  const claimCodeParam = displaysQuery.get('claimCode');
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [isSingleClaimModalOpen, setIsSingleClaimModalOpen] = useState(false);
  const [isBulkClaimModalOpen, setIsBulkClaimModalOpen] = useState(false);
  const handleBackToSelect = () => {
    setIsSingleClaimModalOpen(false);
    setIsBulkClaimModalOpen(false);
    setIsSelectModalOpen(true);
  };
  return (
    <>
      {totalDisplays > 0 && (
        <PageActions
          left={
            <>
              {selectedCount > 0 ? (
                <BulkActionButton selectedCount={selectedCount} onClick={openBulkActions} />
              ) : null}
              <DisplayTableServerSide.Filter
                table={table}
                data={data}
                filtersState={filters}
                handleSetFilters={handleSetFilters}
                onFiltersReady={onFiltersReady}
              />
            </>
          }
          right={
            claimCodeParam ? null : (
              <Shield requiredPermissions={[Permission.DisplayClaim]}>
                <Button variant="solid" colorScheme="blue" onClick={handleClaimDisplay}>
                  {t('ClaimDisplay')}
                </Button>
              </Shield>
            )
          }
          searchbar={
            <DisplayTableServerSide.SearchSelector
              search={search}
              handleSetSearch={handleSetSearch}
            />
          }
        />
      )}

      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
      >
        {totalDisplays === 0 ? (
          <VStack paddingTop={20} paddingBottom={20} spacing={8}>
            <EmptyView
              icon={<EmptyDisplayIllustration />}
              title={t('claimFirstDisplay')}
              description={`${t('waveAppInstructions')}${
                hasDisplayClaimPermission ? '' : ` ${t('permissionNote')}`
              }`}
              padding="0"
            >
              <EmptyViewButton
                label={t('ClaimDisplay')}
                onClick={handleClaimDisplay}
                isDisabled={!hasDisplayClaimPermission}
              />
            </EmptyView>
            <Link
              isExternal
              href="https://docs.wave.ppds.com/"
              color="blue.500"
              target="_blank"
              rel="noreferrer"
              display="flex"
              alignItems="center"
            >
              <HelpIcon color="blue.500" width={5} height={5} marginRight={2} />
              {t('needHelp')}
            </Link>
          </VStack>
        ) : (
          <DisplayTableServerSide
            table={table}
            onGoToDetail={(id) => handleDisplayClicked(id)}
            invalidDisplayIds={extractInvalidDisplayIds(error?.graphQLErrors)}
          />
        )}
      </Box>
      <Box display="flex" marginTop="4">
        <Box flex="1">
          <DisplayTableServerSide.PageSizeSelector
            table={table}
            handleSetPageSize={handleSetPageSize}
            pageSize={pageSize}
          />
        </Box>
        <DisplayTableServerSide.Pagination
          pageIndex={pageIndex}
          totalRowCount={totalRowCount}
          handleSetPageIndex={handleSetPageIndex}
          pageSize={pageSize}
        />
      </Box>
      {
        // Remove once subscriptions are released to everyone: https://inthepocket.atlassian.net/browse/TPVWAVE-1460
      }
      {isSubscriptionsEnabled && hasLiteSubscription ? (
        <UpgradeToWaveEssentialModal
          isOpen={upgradeToWaveEssentialModal.isOpen}
          onClose={upgradeToWaveEssentialModal.onClose}
        />
      ) : (
        <BulkActionModal
          customer={data?.customer as BulkActionModal_CustomerFragment}
          displays={table.selectedFlatRows.map(
            (row) => row.original as unknown as BulkActionModal_DisplayFragment,
          )}
          organization={data?.organization as BulkActionModal_OrganizationFragment}
          isOpen={bulkActionModal.isOpen}
          onCancel={bulkActionModal.onClose}
          onSuccess={handleBulkActionSucceeded}
          subscription={hasLiteSubscription}
        />
      )}

      <SelectClaimModal
        customer={data?.customer}
        isOpen={isSelectModalOpen}
        onClose={() => setIsSelectModalOpen(false)}
        onSelectSingle={() => {
          setIsSelectModalOpen(false);
          setIsSingleClaimModalOpen(true);
        }}
        onSuccess={handleCreateSuccess}
      />
      <BulkClaimModal
        customer={data?.customer as BulkClaimDisplayModal_CustomerFragment}
        customerSite={data?.customer as BulkClaimDisplayModal_CustomerFragment}
        configuration={data?.customer as BulkClaimDefaultConfig_CustomerFragment}
        code={bulkClaimCode}
        id={bulkClaimId}
        isOpen={isBulkClaimModalOpen}
        onCancel={bulkClaimModal.onClose}
        onSuccess={handleMultiClaimCodeActivate}
        onBack={handleBackToSelect}
      />
      <ClaimDisplayModal
        customer={data?.customer as BulkActionModal_CustomerFragment}
        isOpen={isSingleClaimModalOpen}
        onCancel={handleClose}
        onSuccess={handleClaimDisplaySucceeded}
        onBack={handleBackToSelect}
      />
    </>
  );
}

const POLL_INTERVAL = 10000;

export function CustomerDisplaysIndexPage() {
  const { customer, configurationId } = useParams();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { displaysQuery } = useContext(DisplaysQueryContext);
  const claimCodeParam = displaysQuery.get('claimCode');
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState('');
  // Track whether initial persisted filters have been applied
  // This is set to true by the onFiltersReady callback from FilterServerSide
  const filterInitKeyRef = useRef<string>('');
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);

  const [filters, setFilters] = useState<DisplayFilters>([
    { id: 'firmware', value: [] },
    { id: 'powerSchedule', value: [] },
    { id: 'playlist', value: [] },
    { id: 'groups', value: [] },
    { id: 'warnings', value: [] },
    { id: 'site', value: [] },
    { id: 'status', value: [] },
    { id: 'multiClaimIds', value: [] },
    { id: 'configuration', value: [] },
  ]);
  interface TransformedResult {
    firmwares: string[];
    groupIds: string[];
    playlistIds: string[];
    scheduleIds: string[];
    siteIds: string[];
    status: string[];
    warnings: string[];
    multiClaimIds: string[];
    configurationIds: string[];
  }
  // Transform function with proper types
  function transformFiltersData(filters: DisplayFilters): TransformedResult {
    const transformed: TransformedResult = {
      firmwares: [],
      groupIds: [],
      playlistIds: [],
      scheduleIds: [],
      siteIds: [],
      status: [],
      warnings: [],
      multiClaimIds: [],
      configurationIds: [],
    };
    if (claimCodeParam) {
      const id = claimCodeParam.split('|')[2];
      transformed.multiClaimIds.push(id);
    }
    filters.forEach((filter) => {
      switch (filter.id) {
        case 'firmware':
          transformed.firmwares = filter.value.map((option) => option.id);
          break;
        case 'groups':
          transformed.groupIds = filter.value.map((option) => option.id);
          break;
        case 'playlist':
          transformed.playlistIds = filter.value.map((option) => option.id);
          break;
        case 'powerSchedule':
          transformed.scheduleIds = filter.value.map((option) => option.id);
          break;
        case 'site':
          transformed.siteIds = filter.value.map((option) => option.id);
          break;
        case 'warnings':
          transformed.warnings = filter.value.map((option) => option.id);
          break;
        case 'status':
          transformed.status = filter.value.map((option) => option.id);
          break;
        case 'configuration':
          transformed.configurationIds = filter.value.map((option) => option.id);
          break;
        default:
          break;
      }
    });

    return transformed;
  }
  const transformedData = transformFiltersData(filters);
  const handleSetPageSize = (pageSize: number) => {
    setPageSize(pageSize);
    setPageIndex(0);
  };
  const handleSetPageIndex = (pageIndex: number) => {
    setPageIndex(pageIndex);
  };

  const handleSetFilters = (
    filterId: string,
    newValue: FilterOptionServerSide[],
    resetPagination = true,
  ) => {
    if (resetPagination) {
      setPageIndex(0);
    }
    setFilters((prevFilters) =>
      prevFilters.map((filter) =>
        filter.id === filterId ? { ...filter, value: newValue } : filter,
      ),
    );
  };

  // ---start--- this code is for persisiting the filter via browser URL using useDisplaysQuery.tsx ------

  // const filtersFromUrl = getFiltersFromUrl();
  // const transformFiltersToPersistFormat = (
  //   filtersFromUrl: TransformedResult
  // ): DisplayFilters => {
  //   const createFilterOption = (
  //     column: string,
  //     values: string[]
  //   ): FilterOptionServerSide[] =>
  //     values.map((value) => ({
  //       column: column as FilterKeys,
  //       label: value,
  //       value: value,
  //       id: value
  //     }));

  //   return [
  //     { id: 'firmware', value: createFilterOption('firmware', filtersFromUrl.firmwares) },
  //     { id: 'powerSchedule', value: createFilterOption('powerSchedule', filtersFromUrl.scheduleIds) },
  //     { id: 'playlist', value: createFilterOption('playlist', filtersFromUrl.playlistIds) },
  //     { id: 'groups', value: createFilterOption('groups', filtersFromUrl.groupIds) },
  //     { id: 'warnings', value: createFilterOption('warnings', filtersFromUrl.warnings) },
  //     { id: 'site', value: createFilterOption('site', filtersFromUrl.siteIds) },
  //     { id: 'status', value: createFilterOption('status', filtersFromUrl.status) }
  //   ];
  // };
  // const hasUrlFilters = Object.values(filtersFromUrl).some((array) => array.length > 0);
  // const hasFilters = filters.some((filter) => filter.value.length > 0);
  // const { persistFilters} =  useDisplaysQuery();
  // useEffect(() => {
  //   if(hasUrlFilters){
  //     const transformedFilters = transformFiltersToPersistFormat(filtersFromUrl);

  //       persistFilters(transformedFilters);
  //   }else if(hasFilters){
  //       persistFilters(filters);
  //   }

  // }, [filters]);
  // ----end--- this code is for persisiting the filter via browser URL using useDisplaysQuery.tsx ------

  const handleSetSearch = (searchText: string) => {
    setPageIndex(0);
    setSearch(searchText);
  };

  const resetFilters = () => {
    setFilters([
      { id: 'firmware', value: [] },
      { id: 'powerSchedule', value: [] },
      { id: 'playlist', value: [] },
      { id: 'groups', value: [] },
      { id: 'warnings', value: [] },
      { id: 'site', value: [] },
      { id: 'status', value: [] },
      { id: 'configuration', value: [] },
    ]);
  };

  // Determine filter context based on current route
  const isInConfigurationSummary = Boolean(configurationId) && pathname.endsWith('/config-devices');
  const claimCodeId = claimCodeParam ? claimCodeParam.split('|')[2] : undefined;
  const filterContext = claimCodeParam
    ? FilterContext.CLAIM_CODES_DISPLAY
    : isInConfigurationSummary
    ? FilterContext.CONFIGURATION
    : FilterContext.DISPLAYS;

  // Load persisted filters from localStorage using customerHandle (from URL).
  useEffect(() => {
    if (!customer || !user?.subId) return;

    const initKey = `${customer}:${filterContext}:${claimCodeId ?? ''}`;
    if (filterInitKeyRef.current === initKey) return;
    filterInitKeyRef.current = initKey;

    // Reset all filters before loading for the new context/claim code
    setIsFiltersInitialized(false);
    setFilters([
      { id: 'firmware', value: [] },
      { id: 'powerSchedule', value: [] },
      { id: 'playlist', value: [] },
      { id: 'groups', value: [] },
      { id: 'warnings', value: [] },
      { id: 'site', value: [] },
      { id: 'status', value: [] },
      { id: 'multiClaimIds', value: [] },
      { id: 'configuration', value: [] },
    ]);

    async function loadAndApplyFilters() {
      if (!user?.subId || !customer) return;

      const persisted = await getPersistentFilters(
        user.subId,
        customer,
        filterContext,
        claimCodeId,
      );

      if (Object.keys(persisted).length > 0) {
        setFilters((prevFilters) =>
          prevFilters.map((filter) => {
            const persistedValues = persisted[filter.id];
            if (persistedValues && persistedValues.length > 0) {
              return {
                ...filter,
                value: persistedValues.map((v) => ({
                  ...v,
                  id: v.id,
                  value: v.id,
                  column: filter.id,
                })) as FilterOptionServerSide[],
              };
            }
            return filter;
          }),
        );
      }

      setIsFiltersInitialized(true);
    }

    loadAndApplyFilters();
  }, [customer, user, filterContext, claimCodeId]);

  const { data, loading, error, refetch, startPolling, stopPolling } =
    useCustomerDisplaysIndexPageQuery({
      variables: {
        customerHandle: ensure(customer),
        pageNumber: pageIndex,
        pageSize: pageSize,
        searchTerm: search,
        filter: transformedData,
      },
      skip: !isFiltersInitialized, // Skip query until filters are loaded
      pollInterval: POLL_INTERVAL,
      errorPolicy: 'all',
    });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const totalRowCount = data?.customer?.totalFilteredDisplaysCount || 0;

  const totalDisplays = data?.customer?.totalDisplaysCount || 0;

  return (
    <Page title="Displays" pageName="displays_overview">
      <DisplayTableProvider>
        {!loading && !error && data?.customer && (
          <PageContent backgroundColor="gray.50">
            <DisplayOverviewHeadingServerSide
              data={data as DataWithCustomer}
              filtersState={filters}
              handleSetFilters={handleSetFilters}
              resetFilters={resetFilters}
            />
          </PageContent>
        )}
        <PageContent>
          {loading || !isFiltersInitialized ? (
            <PageLoader />
          ) : error ? (
            <HandleApiError error={error} />
          ) : !isDefined(data?.customer) ? (
            <PageLoader />
          ) : (
            <DisplayOverviewServerSide
              data={data as DataWithCustomer}
              error={error}
              refetch={refetch}
              pageSize={pageSize}
              pageIndex={pageIndex}
              totalRowCount={totalRowCount}
              totalDisplays={totalDisplays}
              filters={filters}
              search={search}
              handleSetSearch={handleSetSearch}
              handleSetPageSize={handleSetPageSize}
              handleSetPageIndex={handleSetPageIndex}
              handleSetFilters={handleSetFilters}
            />
          )}
        </PageContent>
      </DisplayTableProvider>
    </Page>
  );
}

CustomerDisplaysIndexPage.graphql = {
  queries: {
    CustomerDisplaysIndexPage: gql`
      query CustomerDisplaysIndexPage(
        $customerHandle: String!
        $pageNumber: Int
        $pageSize: Int
        $searchTerm: String
        $filter: DisplayFilters
      ) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          name
          ...ClaimDisplayModal_customer
          ...BulkActionModal_customer
          displays(
            pageNumber: $pageNumber
            pageSize: $pageSize
            searchTerm: $searchTerm
            filter: $filter
          ) {
            id
            serialNumber
            agentVersion
            networkInformation {
              ethernetMacAddress
              wifiMacAddress
              localIPAddress
            }
            ...DisplayTable_display
            ...BulkActionModal_display
          }
          supportedPlatforms
          configurations {
            id
            name
            state
            platform
          }
          assignedConfigurations {
            id
            name
            state
          }
          displayCount
          waveSubscriptionUsage
          waveSubscriptions {
            id
            validUntil
            status
          }
          warningList {
            key
            label
          }
          totalDisplaysCount
          totalFilteredDisplaysCount(
            pageNumber: $pageNumber
            pageSize: $pageSize
            searchTerm: $searchTerm
            filter: $filter
          )
          waveSubscription {
            usage {
              current
              max
            }
          }
          displayUsage {
            presence {
              connected
              disconnected
            }
            powerState {
              plannedOn
              plannedStandBy
              unplannedOn
              unplannedStandBy
              disconnected
            }
            contentSource {
              standby
              disconnected
              sources {
                source
                count
              }
            }
          }
          displayFirmwareVersions
        }
        organization {
          ...BulkActionModal_organization
        }
      }
    `,
  },
};
